import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { i18next } from '@/app/i18n';
import { localeCatalog } from '@/app/i18n/localesCatalog';
import { supportedLocales, type SupportedLocale } from '@/app/i18n/resources';
import { useSettingsStore, useThemeColors } from '@/settings/handler/settingsStore';
import { PixelContainer } from '@/ui/pixel/PixelContainer';

export function LanguagePicker() {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const locale = useSettingsStore((state) => state.locale);
  const setLocale = useSettingsStore((state) => state.setLocale);
  const [query, setQuery] = useState('');

  const items = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return localeCatalog.filter((entry) => {
      if (!supportedLocales.includes(entry.id as SupportedLocale)) return false;
      if (!needle) return true;
      return (
        entry.endonym.toLowerCase().includes(needle) ||
        entry.id.toLowerCase().includes(needle) ||
        entry.language.toLowerCase().includes(needle)
      );
    });
  }, [query]);

  return (
    <div className="flex flex-col gap-2">
      <PixelContainer radius={3} pixelScale={2} padding="8px 12px">
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          placeholder={t('languageSearch')}
          className="w-full bg-transparent text-[20px] leading-none outline-none"
          style={{ color: colors.onSurface, fontFamily: 'inherit' }}
        />
      </PixelContainer>
      <div className="flex max-h-72 flex-col gap-2 overflow-y-auto">
        {items.map((entry) => {
          const selected = entry.id === locale;
          return (
            <PixelContainer
              key={entry.id}
              radius={3}
              pixelScale={2}
              padding="10px 12px"
              backgroundColor={
                selected ? colors.surfaceContainerHighest : colors.surfaceContainerHigh
              }
              borderColor={selected ? colors.onSurface : colors.outlineVariant}
              onClick={() => {
                const id = entry.id as SupportedLocale;
                setLocale(id);
                void i18next.changeLanguage(id);
              }}
            >
              <div className="flex items-center gap-3">
                <span className="text-[22px] leading-none">{entry.flag}</span>
                <span className="flex-1 text-[22px] leading-none">{entry.endonym}</span>
                {selected ? <span className="text-[18px] leading-none">✓</span> : null}
              </div>
            </PixelContainer>
          );
        })}
      </div>
    </div>
  );
}
